"use client";

import { Bell, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/hooks/notifications-context";
import { aparenciaDoTipo, type AppNotification } from "@/lib/notification-types";

/** "agora", "há 2 min", "há 3 h", "há 5 d" — sem dependencia de lib de data. */
function tempoRelativo(iso: string): string {
  const seg = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seg < 60) return "agora";
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} d`;
}

/**
 * Sino de notificacoes, no rodape da sidebar (ao lado do toggle de tema).
 *
 * Fica na sidebar, nao num header: o `components/layout/header.tsx` que existia
 * era codigo morto (ninguem o importava) e foi removido em 29/07 — o menu de
 * usuario real da plataforma vive aqui.
 */
export function NotificationBell() {
  const router = useRouter();
  const [aberto, setAberto] = useState(false);
  const {
    items,
    unreadCount,
    temMais,
    carregandoMais,
    carregarMais,
    markRead,
    markAllRead,
    remove,
  } = useNotifications();

  const abrirItem = (n: AppNotification) => {
    markRead(n.id);
    // Notificacao sem link e informativa (ex.: "campanha finalizada") — marca
    // como lida e mantem o popover aberto pro usuario ver o resto.
    if (!n.link) return;
    setAberto(false);
    router.push(n.link);
  };

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              unreadCount > 0
                ? `Notificações, ${unreadCount} não lidas`
                : "Notificações"
            }
            className="relative h-8 w-8 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span
                data-testid="notification-badge"
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
        }
      />

      {/* p-0 e gap-0 sobrescrevem o padding/gap default do PopoverContent, que e
          pensado pra conteudo em coluna espacada — aqui as linhas encostam. */}
      <PopoverContent align="end" side="right" className="w-80 gap-0 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium">Notificações</span>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <Bell className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => {
              const { icone: Icone, cor } = aparenciaDoTipo(n.type);
              return (
                <div
                  key={n.id}
                  className={cn(
                    "group flex items-start gap-2.5 border-b border-border px-3 py-2.5 last:border-0",
                    !n.readAt && "bg-accent/5",
                  )}
                >
                  <Icone className={cn("mt-0.5 h-4 w-4 shrink-0", cor)} />
                  <button
                    onClick={() => abrirItem(n)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      {!n.readAt && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      )}
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="truncate text-xs text-muted-foreground">{n.body}</p>
                    )}
                    <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                      {tempoRelativo(n.createdAt)}
                    </p>
                  </button>
                  <button
                    aria-label="Apagar notificação"
                    onClick={() => remove(n.id)}
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent/20 hover:text-foreground group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}

            {temMais && (
              <button
                onClick={carregarMais}
                disabled={carregandoMais}
                className="w-full py-2.5 text-xs text-muted-foreground transition-colors hover:bg-accent/10 hover:text-foreground disabled:opacity-50"
              >
                {carregandoMais ? "Carregando…" : "Carregar mais"}
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
